import CustomButton from 'components/CustomInput/Button';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import api from 'api/call';
import useQuery from 'hooks/useQuery';
import { useDispatch, useSelector } from 'react-redux';
import LoadingIndicator from 'components/LoadingIndicator';
import CiDelegationEdit from './CiDelegationEdit';
import PiDelegationEdit from './PiDelegationEdit';
import { generateRandomId, produce } from 'utility/general';
import { SAVE_DELEGATION } from 'store/key';
import useDialog from 'components/ConfirmDialog/useDialog';
import { CONFIRM_UPDATE_DELEGATE, DELEGATION_UPDATED } from 'utility/frontendMessage';
import { setGlobalLoading } from 'store/global/reducer';
import { useHistory } from 'react-router';

const SLayout = styled.div`
    padding: 15px;
`;

const getDefaultPostData = () => ({
    postData: {},
    deleteRequest: {},
    removeRequest: {},
    // applicationFormId: -1
});

const ChangeDelegation = (props) => {
    const [isLoading, setLoading] = useState(false);
    const folder = useSelector((state) => state.application.folder);
    // const [isError, setError] = useState(false);
    const [data, setData] = useState({});
    const id = useQuery('id');
    const dispatch = useDispatch();
    const [postData, setPostData] = useState(getDefaultPostData());
    const showDialog = useDialog();
    const history = useHistory();
    const [isTableUpdate, setTableUpdate] = useState(false);

    const getApplicationDelegation = () => {
        return api.application
            .getDelegation(id)
            .then((res) => {
                setData(res.data.result);
            })
            .catch((err) => {})
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        setLoading(true);
        getApplicationDelegation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return (
            <div
                style={{ height: 300, display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center' }}
            >
                <LoadingIndicator />
            </div>
        );
    }

    const onCiAdd = (data) => {
        data.id = 'temp_' + generateRandomId();

        // update local data
        setData(
            produce((draft) => {
                draft.study.push(data);
            }),
        );

        // update postData when submit
        setPostData(
            produce((draft) => {
                if (!draft.postData.ciDelegation) {
                    draft.postData.ciDelegation = [];
                }

                draft.postData.ciDelegation.push(data);
            }),
        );
    };

    const onCiEdit = (data) => {
        setData(
            produce((draft) => {
                const index = draft.study.findIndex((item) => {
                    return item.id === data.id;
                });

                draft.study[index] = data;
            }),
        );

        // update postData when submit
        setPostData(
            produce((draft) => {
                // check if postData exist
                const index = draft.postData?.ciDelegation?.findIndex((item) => item.id === data.id);

                // deleted data is not in remote data
                if (index >= 0) {
                    draft.postData.ciDelegation[index] = data;
                } else {
                    if (!draft.postData.ciDelegation) {
                        draft.postData.ciDelegation = [];
                    }

                    draft.postData.ciDelegation.push(data);
                }
            }),
        );
    };

    const onCiDelete = (data) => {
        // update local data
        setData(
            produce((draft) => {
                const index = draft.study.findIndex((item) => {
                    return item.id === data.id;
                });

                draft.study.splice(index, 1);
            }),
        );

        // update postData when submit
        setPostData(
            produce((draft) => {
                // check if postData exist
                const index = draft.postData?.ciDelegation?.findIndex((item) => item.id === data.id);

                // deleted data is not in remote data
                if (index >= 0) {
                    draft.postData.ciDelegation.splice(index, 1);
                    if (draft.postData.ciDelegation.length === 0) {
                        delete draft.postData.ciDelegation;
                    }
                }

                if (!data.id.toString().startsWith('temp_')) {
                    if (!draft.deleteRequest.ciDelegation) {
                        draft.deleteRequest.ciDelegation = [];
                    }

                    draft.deleteRequest.ciDelegation.push({ id: data.id });
                }
            }),
        );
    };

    const onSiteAdd = ({ institution, data, siteInfoId }) => {
        data.id = 'temp_' + generateRandomId();

        setData(
            produce((draft) => {
                draft.siteInfo[institution].data.push(data);
            }),
        );

        // update postData when submit
        setPostData(
            produce((draft) => {
                if (!draft.postData.piDelegation) {
                    draft.postData.piDelegation = [];
                }

                data.siteInfo = { id: siteInfoId };
                draft.postData.piDelegation.push(data);
            }),
        );
    };

    const onSiteEdit = ({ institution, data, siteInfoId }) => {
        setData(
            produce((draft) => {
                const index = draft.siteInfo[institution].data.findIndex((item) => {
                    return item.id === data.id;
                });

                draft.siteInfo[institution].data[index] = data;
            }),
        );

        // update postData when submit
        setPostData(
            produce((draft) => {
                // check if postData exist
                const index = draft.postData?.piDelegation?.findIndex((item) => item.id === data.id);

                data.siteInfo = { id: siteInfoId };

                // deleted data is not in remote data
                if (index >= 0) {
                    draft.postData.piDelegation[index] = data;
                } else {
                    if (!draft.postData.piDelegation) {
                        draft.postData.piDelegation = [];
                    }

                    draft.postData.piDelegation.push(data);
                }
            }),
        );
    };

    const onSiteDelete = ({ institution, data, siteInfoId }) => {
        setData(
            produce((draft) => {
                const index = draft.siteInfo[institution].data.findIndex((item) => {
                    return item.id === data.id;
                });

                draft.siteInfo[institution].data.splice(index, 1);
            }),
        );

        // update postData when submit
        setPostData(
            produce((draft) => {
                // check if postData exist
                const index = draft.postData?.piDelegation?.findIndex((item) => item.id === data.id);

                // deleted data is not in remote data
                if (index >= 0) {
                    draft.postData.piDelegation.splice(index, 1);
                    if (draft.postData.piDelegation.length === 0) {
                        delete draft.postData.piDelegation;
                    }
                }

                if (!data.id.toString().startsWith('temp_')) {
                    if (!draft.deleteRequest.piDelegation) {
                        draft.deleteRequest.piDelegation = [];
                    }

                    draft.deleteRequest.piDelegation.push({ id: data.id, siteInfo: { id: siteInfoId } });
                }
            }),
        );
    };

    const onSubmit = async () => {
        const isConfirm = await showDialog({
            description: CONFIRM_UPDATE_DELEGATE,
        });

        if (isConfirm) {
            setGlobalLoading(true);

            dispatch({
                type: SAVE_DELEGATION,
                payload: {
                    id,
                    dataToUpdate: postData,
                    callback: () => {
                        showDialog({
                            description: DELEGATION_UPDATED,
                            disableCancelButton: true,
                        });

                        setPostData(getDefaultPostData());

                        setTableUpdate(true);
                        getApplicationDelegation().finally(() => {
                            setTableUpdate(false);
                        });
                    },
                    final: () => {
                        setGlobalLoading(false);
                    },
                },
            });
        }
    };

    return (
        <SLayout>
            <div>
                <CustomButton onClick={onSubmit}>Submit</CustomButton>
                <CustomButton
                    type={'cancel'}
                    style={{ marginLeft: 25 }}
                    onClick={() => {
                        history.push(`/applications/${folder || 'all'}`);
                    }}
                >
                    Back
                </CustomButton>
            </div>

            {data.study && (
                <div style={{ marginTop: 30 }}>
                    <CiDelegationEdit
                        piEmailList={data.piList}
                        isTableLoading={isTableUpdate}
                        delegationData={data.study}
                        onCiDelegateAdd={onCiAdd}
                        onCiDelegateUpdate={onCiEdit}
                        onCiDelegateRemove={onCiDelete}
                    />
                </div>
            )}

            {data.siteInfo &&
                Object.entries(data.siteInfo)?.map(([institutionKey, dataValue], index) => {
                    // if data === null, it means the user has no permission for that site
                    if (!dataValue) {
                        return null;
                    }

                    return (
                        <div style={{ marginTop: 30 }} key={dataValue.id + index}>
                            <PiDelegationEdit
                                isTableLoading={isTableUpdate}
                                siteInfoId={dataValue.id}
                                delegationData={dataValue.data}
                                siteDisplay={institutionKey}
                                onPiDelegateAdd={onSiteAdd}
                                onPiDelegateUpdate={onSiteEdit}
                                onPiDelegateRemove={onSiteDelete}
                            />
                        </div>
                    );
                })}
        </SLayout>
    );
};

export default ChangeDelegation;
